import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import DOMPurify from 'isomorphic-dompurify';
import * as Styled from './MTimer.styles';
import { MTimerProps, MTimerAlignment, TimeLeft } from './MTimer.types';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ADigit } from '@marriott/mi-web-ui-kit';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ADigitSize } from '@marriott/mi-web-ui-kit';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ABody } from '@marriott/mi-web-ui-kit';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ABodySize } from '@marriott/mi-web-ui-kit';

const COMPONENT_NAME = 'm-cobrand-MTimer';
const TEST_ID = 'cobrand-MTimer';

/**
 * Strips HTML tags and returns only the text content for screen reader announcements
 * Uses DOMPurify to safely sanitize and extract text content from HTML strings
 * Excludes content from <del> tags as they represent deleted/struck-through content
 * @param html - HTML string that may contain tags like <del>, <p>, etc.
 * @returns Plain text content suitable for screen readers
 */
const getAccessibleText = (html: string | undefined): string => {
  if (!html) return '';

  // Use DOMPurify to sanitize and get a DOM element
  const cleanEl = DOMPurify.sanitize(html, {
    RETURN_DOM: true,
  }) as unknown as HTMLElement;

  // Remove deleted/struck-through elements and their content
  const deletedElements = cleanEl.querySelectorAll('del, strike, s');
  deletedElements.forEach(el => el.remove());

  // Extract text content and normalize whitespace (replace multiple spaces with single space)
  return (cleanEl.textContent || '').trim().replace(/\s+/g, ' ');
};

const calculateTimeLeft = (targetDate: string): TimeLeft => {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
};

export const MTimer: FC<MTimerProps> = ({
  alignment = MTimerAlignment.Centered,
  aDigit,
  aBody,
  className,
  targetDate,
  daysLabel = 'DAYS',
  hoursLabel = 'HOURS',
  minutesLabel = 'MINUTES',
  secondsLabel = 'SECONDS',
  updateInterval = 1000,
  screenReaderInterval = 300000, // 5 minutes
  onComplete,
  isAuthorMode = false,
  isLoading = false,
  bodyText,
  ...rest
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    targetDate ? calculateTimeLeft(targetDate) : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );

  // Start empty so the aria-live region makes no announcement on initial render.
  // The screen reader reads the visible DOM content naturally; programmatic
  // announcements only fire after the first screenReaderInterval (5 minutes).
  const [announcementText, setAnnouncementText] = useState('');

  const [isClient, setIsClient] = useState(false);

  // Use refs to avoid recreating updateTime callback
  // Initialize to Date.now() so the first tick comparison (now - lastAnnouncement)
  // starts near zero, preventing an immediate announcement on mount.
  const lastAnnouncementRef = useRef(Date.now());
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  // Keep the ref up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Hydration fix: Detect client-side rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  const updateTime = useCallback(() => {
    if (!targetDate) return;

    const newTimeLeft = calculateTimeLeft(targetDate);
    setTimeLeft(newTimeLeft);

    // Check if countdown is complete
    if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
      if (onCompleteRef.current && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onCompleteRef.current();
      }
    }

    // Update screen reader announcement every 5 minutes with timer only
    const now = Date.now();

    const shouldAnnounce = now - lastAnnouncementRef.current >= screenReaderInterval;

    if (shouldAnnounce) {
      // All announcements include bodyText (if provided) followed by timer
      const timeLeftText = `${newTimeLeft.days} days, ${newTimeLeft.hours} hours, ${newTimeLeft.minutes} minutes remaining`;
      // Strip HTML tags to get accessible text only
      const accessibleBodyText = getAccessibleText(bodyText);
      const announcement = accessibleBodyText ? `${accessibleBodyText}. ${timeLeftText}` : timeLeftText;
      setAnnouncementText(announcement);
      lastAnnouncementRef.current = now;
    }
  }, [targetDate, screenReaderInterval, bodyText]);

  useEffect(() => {
    if (!isClient || isAuthorMode || !targetDate) {
      // Don't start countdown until client-side or if in author mode or no targetDate
      return;
    }

    // Reset completion flag when targetDate changes
    hasCompletedRef.current = false;

    updateTime();
    const interval = setInterval(updateTime, updateInterval);

    return () => clearInterval(interval);
  }, [updateTime, updateInterval, isAuthorMode, isClient, targetDate]);

  const formatNumber = (num: number): string => String(num).padStart(2, '0');

  // If no targetDate, render static display (backward compatible)
  if (!targetDate) {
    const alignmentClass = alignment === MTimerAlignment.Centered ? 'align-items-center' : 'align-items-start';

    return (
      <Styled.Wrapper
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        alignment={alignment}
        className={`d-flex flex-column ${alignmentClass}${className ? ` ${className}` : ''}`}
        {...rest}
      >
        {aDigit && <ADigit {...aDigit} size={aDigit.size ?? ADigitSize.Large} className="pb-0" />}
        {aBody?.bodyText ? <ABody {...aBody} /> : null}
      </Styled.Wrapper>
    );
  }

  // Show skeleton loader during SSR to avoid hydration mismatch OR when explicitly loading
  if (!isClient || isLoading) {
    return (
      <Styled.SkeletonWrapper
        data-component-name={COMPONENT_NAME}
        data-testid={`${TEST_ID}-skeleton`}
        className={className}
      >
        <Styled.ScreenReaderOnly>Loading countdown timer</Styled.ScreenReaderOnly>
        <Styled.TimerDisplay aria-hidden="true">
          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>
        </Styled.TimerDisplay>
      </Styled.SkeletonWrapper>
    );
  }

  // Render countdown display
  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={className}>
      {/* Visual timer - completely hidden from screen readers to avoid duplication */}
      <Styled.TimerDisplay aria-hidden="true">
        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.days)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={daysLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.hours)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={hoursLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.minutes)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={minutesLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.seconds)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={secondsLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>
      </Styled.TimerDisplay>

      {/* Static: read once in natural SR flow when the user first encounters the timer */}
      <Styled.ScreenReaderOnly>
        {`${timeLeft.days} days, ${timeLeft.hours} hours, ${timeLeft.minutes} minutes remaining.`}
      </Styled.ScreenReaderOnly>

      {/* Polite live region: silent on load, announces every 5 minutes */}
      <Styled.ScreenReaderOnly aria-live="polite" aria-atomic="true">
        {announcementText}
      </Styled.ScreenReaderOnly>
    </Styled.Wrapper>
  );
};

export { MTimerAlignment, TEST_ID as M_TIMER_TEST_ID };
